import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, AlertTriangle, Eye, Clock, TrendingDown, BookOpen, CheckCircle } from 'lucide-react';
import { BrutalCard, BrutalButton, BrutalBadge } from '@/components/brutal';
import { integrityAPI, studentAPI, narrativeAPI, reflectionAPI } from '@/lib/api';
import { toast } from 'sonner';

const SIGNAL_LABELS = {
  paste_from_source: { label: 'Copied from story', icon: AlertTriangle, color: 'amber' },
  paste_from_feedback: { label: 'Copied from hint (loophole)', icon: AlertTriangle, color: 'rose' },
  paste_event: { label: 'Paste detected', icon: Eye, color: 'amber' },
  speed_fast: { label: 'Reading impossibly fast', icon: TrendingDown, color: 'amber' },
  speed_slow: { label: 'Reading impossibly slow (walked away?)', icon: Clock, color: 'amber' },
  speed_inconsistent: { label: 'Wild reading-speed swings', icon: TrendingDown, color: 'rose' },
  trivial_answer: { label: 'Single-letter / gave-up answer', icon: AlertTriangle, color: 'amber' },
  retry_farming: { label: 'Gaming the retry system', icon: AlertTriangle, color: 'rose' },
  concerning_content: { label: 'Answer to review with child', icon: AlertTriangle, color: 'rose' },
  typing_too_fast: { label: 'Typing faster than expected', icon: TrendingDown, color: 'amber' },
  typing_rhythm_change: { label: 'Typing rhythm differs from usual', icon: Eye, color: 'amber' },
  typing_no_revisions: { label: 'Typed with no pauses or fixes', icon: Eye, color: 'amber' },
  followup_mismatch: { label: 'Spoken answers did not match the writing', icon: AlertTriangle, color: 'rose' },
};

const fmtSeconds = (s) => {
  if (!s) return '0m';
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
};

const IntegrityReportTab = () => {
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [view, setView] = useState('weekly'); // 'weekly' | 'by-story'
  const [selectedNarrativeId, setSelectedNarrativeId] = useState(null);
  const [sinceDays, setSinceDays] = useState(7);

  const { data: students } = useQuery({
    queryKey: ['students-for-integrity'],
    queryFn: async () => (await studentAPI.getAll()).data,
  });

  const studentList = students?.students || students || [];

  // Prefetch weekly summary (currently only used as a background heartbeat query;
  // full signal list below drives the visible summary counts).
  useQuery({
    queryKey: ['integrity-weekly', selectedStudentId, sinceDays],
    queryFn: async () => (await integrityAPI.signalsSummary(selectedStudentId, sinceDays)).data,
    enabled: !!selectedStudentId && view === 'weekly',
  });

  const { data: signals, isLoading: loadingSignals } = useQuery({
    queryKey: ['integrity-signals', selectedStudentId, sinceDays],
    queryFn: async () => {
      const res = await integrityAPI.listSignals(selectedStudentId, { since_days: sinceDays });
      return res.data;
    },
    enabled: !!selectedStudentId,
  });

  const { data: narratives } = useQuery({
    queryKey: ['narratives-for-integrity', selectedStudentId],
    queryFn: async () => (await narrativeAPI.getAll({ student_id: selectedStudentId })).data,
    enabled: !!selectedStudentId && view === 'by-story',
  });

  const { data: rules } = useQuery({
    queryKey: ['integrity-rules', selectedStudentId],
    queryFn: async () => (await integrityAPI.getEffectiveRules(selectedStudentId)).data,
    enabled: !!selectedStudentId,
  });

  const { data: sessions } = useQuery({
    queryKey: ['reading-sessions', selectedStudentId, selectedNarrativeId],
    queryFn: async () => (await integrityAPI.listSessions(selectedStudentId, selectedNarrativeId)).data,
    enabled: !!selectedStudentId,
  });

  const totalActive = (sessions?.sessions || []).reduce((a, s) => a + (s.active_seconds || 0), 0);
  const totalWall = (sessions?.sessions || []).reduce((a, s) => a + (s.wall_clock_seconds || 0), 0);
  const totalIdle = (sessions?.sessions || []).reduce((a, s) => a + (s.idle_seconds || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-indigo-500 text-white p-3 border-4 border-black brutal-shadow-md">
          <Shield size={28} />
        </div>
        <div>
          <h2 className="text-3xl font-black uppercase">Integrity Report</h2>
          <p className="text-sm font-medium text-gray-600">
            How honestly your child is engaging with their reading. Not shown to the child.
          </p>
        </div>
      </div>

      {/* Student picker */}
      <BrutalCard>
        <label className="block font-bold uppercase text-xs mb-2">Select Child</label>
        <div className="flex flex-wrap gap-2">
          {studentList.map((s) => (
            <button
              key={s.id}
              onClick={() => { setSelectedStudentId(s.id); setSelectedNarrativeId(null); }}
              className={`px-4 py-2 border-4 border-black font-bold uppercase text-sm ${
                selectedStudentId === s.id ? 'bg-indigo-500 text-white' : 'bg-white'
              }`}
            >
              {s.full_name || s.name}
            </button>
          ))}
        </div>
      </BrutalCard>

      {!selectedStudentId && (
        <BrutalCard className="text-center py-12 text-gray-500">
          <p className="font-bold">Pick a child above to see their integrity report.</p>
        </BrutalCard>
      )}

      {selectedStudentId && (
        <>
          {/* View toggle + window */}
          <div className="flex flex-wrap gap-2 items-center">
            <BrutalButton
              variant={view === 'weekly' ? 'indigo' : 'ghost'}
              onClick={() => setView('weekly')}
            >
              Weekly Summary
            </BrutalButton>
            <BrutalButton
              variant={view === 'by-story' ? 'indigo' : 'ghost'}
              onClick={() => setView('by-story')}
            >
              Per-Story Report
            </BrutalButton>
            <div className="ml-auto flex items-center gap-2">
              <label className="text-xs font-bold uppercase">Window:</label>
              <select
                value={sinceDays}
                onChange={(e) => setSinceDays(Number(e.target.value))}
                className="border-4 border-black px-2 py-1 font-bold"
              >
                <option value={1}>1 day</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
          </div>

          {/* Section 1: Reading Behavior */}
          <BrutalCard>
            <h3 className="text-xl font-black uppercase mb-3 flex items-center gap-2">
              <Clock size={20} /> Section 1 · Reading Behavior
            </h3>
            <div className="grid md:grid-cols-3 gap-3">
              <BrutalCard variant="emerald" className="text-center">
                <p className="text-xs font-bold uppercase">Active Reading</p>
                <p className="text-3xl font-black">{fmtSeconds(totalActive)}</p>
                <p className="text-xs mt-1">Actually engaged</p>
              </BrutalCard>
              <BrutalCard variant="amber" className="text-center">
                <p className="text-xs font-bold uppercase">Wall Clock</p>
                <p className="text-3xl font-black">{fmtSeconds(totalWall)}</p>
                <p className="text-xs mt-1">App was open</p>
              </BrutalCard>
              <BrutalCard variant="rose" className="text-center">
                <p className="text-xs font-bold uppercase">Idle / Walked Away</p>
                <p className="text-3xl font-black">{fmtSeconds(totalIdle)}</p>
                <p className="text-xs mt-1">Did not count toward WPM</p>
              </BrutalCard>
            </div>
            <p className="text-xs text-gray-600 mt-3">
              WPM is now calculated from <b>active</b> reading time, not wall-clock. A child who
              leaves the story open and walks away no longer inflates their stats.
            </p>

            {/* Reading timeline: every individual session, start → end */}
            <div className="mt-4">
              <p className="font-bold uppercase text-xs mb-2">Reading Timeline — every session</p>
              {(sessions?.sessions || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic">
                  No heartbeat sessions yet. Sessions are recorded once a child opens a story after the Apr 14 2026 deploy.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-4 border-black">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left">Started</th>
                        <th className="p-2 text-left">Ended</th>
                        <th className="p-2 text-right">Chapter</th>
                        <th className="p-2 text-right">Active</th>
                        <th className="p-2 text-right">Wall</th>
                        <th className="p-2 text-right">Idle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.sessions.slice(0, 100).map((s) => {
                        const start = s.started_at ? new Date(s.started_at) : null;
                        const end = s.ended_at ? new Date(s.ended_at) : (s.last_heartbeat_at ? new Date(s.last_heartbeat_at) : null);
                        return (
                          <tr key={s.id} className="border-t-2 border-black">
                            <td className="p-2">{start ? start.toLocaleString() : '—'}</td>
                            <td className="p-2">{end ? end.toLocaleString() : '(open)'}</td>
                            <td className="p-2 text-right">Ch.{s.chapter_number}</td>
                            <td className="p-2 text-right font-bold">{fmtSeconds(s.active_seconds || 0)}</td>
                            <td className="p-2 text-right text-gray-500">{fmtSeconds(s.wall_clock_seconds || 0)}</td>
                            <td className="p-2 text-right text-rose-600">{fmtSeconds(s.idle_seconds || 0)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </BrutalCard>

          {/* Section 2: Integrity Signals */}
          <BrutalCard>
            <h3 className="text-xl font-black uppercase mb-3 flex items-center gap-2">
              <AlertTriangle size={20} /> Section 2 · Integrity Signals
            </h3>
            {loadingSignals && <p>Loading…</p>}
            {signals && signals.count === 0 && (
              <div className="flex items-center gap-2 text-emerald-700 font-bold">
                <CheckCircle size={20} /> No integrity signals detected in this window. 👏
              </div>
            )}
            {signals && signals.count > 0 && (
              <div className="space-y-3">
                {Object.entries(signals.counts_by_type || {}).map(([type, count]) => {
                  const meta = SIGNAL_LABELS[type] || { label: type, icon: AlertTriangle, color: 'amber' };
                  const Icon = meta.icon;
                  return (
                    <div key={type} className="flex items-start gap-3 border-4 border-black p-3 bg-white">
                      <Icon size={20} />
                      <div className="flex-1">
                        <p className="font-bold">{meta.label}</p>
                        <p className="text-xs text-gray-600">Detected {count} time{count === 1 ? '' : 's'}</p>
                      </div>
                      <BrutalBadge variant={meta.color}>{count}</BrutalBadge>
                    </div>
                  );
                })}
                <details className="mt-3">
                  <summary className="cursor-pointer font-bold text-sm uppercase">Show raw signal log</summary>
                  <div className="mt-2 space-y-2 max-h-80 overflow-y-auto text-xs">
                    {signals.signals?.slice(0, 50).map((s) => (
                      <div key={s.id} className="border-l-4 border-black pl-2">
                        <p className="font-bold">{s.signal_type} · {s.severity}</p>
                        <p className="italic">"{s.evidence_excerpt}"</p>
                        {s.source_excerpt && <p className="text-gray-500">Matched against: "{s.source_excerpt}"</p>}
                        <p className="text-gray-400">{new Date(s.detected_at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
          </BrutalCard>

          {/* Section 3: Per-story view */}
          {view === 'by-story' && (
            <BrutalCard>
              <h3 className="text-xl font-black uppercase mb-3 flex items-center gap-2">
                <BookOpen size={20} /> Per-Story Reports
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {(narratives?.narratives || narratives || []).map((n) => (
                  <button
                    key={n.id}
                    onClick={() => setSelectedNarrativeId(n.id)}
                    className={`px-3 py-1 border-4 border-black text-xs font-bold ${
                      selectedNarrativeId === n.id ? 'bg-indigo-500 text-white' : 'bg-white'
                    }`}
                  >
                    {n.title}
                  </button>
                ))}
              </div>
              {selectedNarrativeId && (
                <StoryReportView studentId={selectedStudentId} narrativeId={selectedNarrativeId} />
              )}
            </BrutalCard>
          )}

          {/* Settings */}
          {/* Remediation queue — auto-generated stories assigned after failed
              assessments or repeated integrity-pattern hits. */}
          {selectedStudentId && (
            <RemediationPanel studentId={selectedStudentId} />
          )}

          {/* Character reflection answers — what the child actually wrote,
              grouped by axis. Ungraded but deeply revealing. */}
          {selectedStudentId && (
            <ReflectionAnswersPanel studentId={selectedStudentId} />
          )}

          {rules && (
            <BrutalCard>
              <h3 className="text-xl font-black uppercase mb-3">Current Rules (effective for this child)</h3>
              <div className="grid md:grid-cols-2 gap-2 text-sm">
                <div>Pass threshold: <b>{rules.pass_threshold}%</b></div>
                <div>Max attempts before remediation: <b>{rules.max_attempts_before_remediation}</b></div>
                <div>Remediation enabled: <b>{rules.remediation_enabled ? 'Yes' : 'No'}</b></div>
                <div>Honor code: <b>{rules.honor_code_enabled ? 'Yes' : 'No'}</b></div>
                <div>Auto-fail feedback-paste: <b>{rules.auto_fail_feedback_paste ? 'Yes' : 'No'}</b></div>
                <div>Idle cutoff: <b>{rules.heartbeat_idle_seconds}s</b></div>
              </div>
            </BrutalCard>
          )}
        </>
      )}
    </div>
  );
};

const StoryReportView = ({ studentId, narrativeId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['story-report', studentId, narrativeId],
    queryFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrity/report/story/${narrativeId}?student_id=${studentId}`,
        { credentials: 'include', headers: { Authorization: `Bearer ${localStorage.getItem('sv_token') || ''}` } }
      );
      return res.json();
    },
  });

  if (isLoading) return <p>Loading report…</p>;
  if (!data) return null;

  return (
    <div className="space-y-3 text-sm">
      <div>
        <p className="font-bold uppercase text-xs">Honest WPM vs Wall-Clock WPM</p>
        <table className="w-full border-4 border-black mt-2 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Chapter</th>
              <th className="p-2 text-right">Words</th>
              <th className="p-2 text-right">Active time</th>
              <th className="p-2 text-right">WPM (active)</th>
              <th className="p-2 text-right">WPM (wall-clock)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.section_1_reading_behavior?.chapter_wpm || {}).map(([ch, v]) => (
              <tr key={ch} className="border-t-2 border-black">
                <td className="p-2 font-bold">Ch. {ch}</td>
                <td className="p-2 text-right">{v.word_count}</td>
                <td className="p-2 text-right">{fmtSeconds(v.active_seconds)}</td>
                <td className="p-2 text-right">{v.wpm_active ?? '—'}</td>
                <td className="p-2 text-right text-gray-500">{v.wpm_wall_clock ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <p className="font-bold uppercase text-xs">Integrity Examples (for this story)</p>
        {Object.entries(data.section_2_integrity_signals?.examples || {}).map(([type, examples]) => (
          <div key={type} className="border-l-4 border-black pl-3 my-2">
            <p className="font-bold">{SIGNAL_LABELS[type]?.label || type}</p>
            {examples.map((ex, i) => (
              <div key={i} className="my-1 text-xs">
                <p>Child wrote: <span className="italic">"{ex.evidence}"</span></p>
                {ex.source && <p className="text-gray-500">Matched: "{ex.source}"</p>}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div>
        <p className="font-bold uppercase text-xs">Profile Consistency Check</p>
        {(data.section_4_profile_consistency?.evidence || []).map((e, i) => (
          <div key={i} className="border-l-4 border-black pl-3 my-1">
            <p>
              <b>{e.weakness}</b>:{' '}
              {e.consistent ? (
                <span className="text-rose-600 font-bold">
                  Observed — {e.signals.map((s) => `${s.count}× ${s.signal_type}`).join(', ')}
                </span>
              ) : (
                <span className="text-emerald-600">No matching behavior this period</span>
              )}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Shows the remediation queue for a student. Each item maps to a failed
// assessment or integrity pattern; if the AI story hasn't been generated
// yet, a button kicks off generation.
const RemediationPanel = ({ studentId }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['remediation', studentId],
    queryFn: async () => (await integrityAPI.listRemediation(studentId)).data,
    enabled: !!studentId,
  });

  const generateMut = useMutation({
    mutationFn: (queueId) => integrityAPI.generateRemediation(queueId),
    onSuccess: () => {
      queryClient.invalidateQueries(['remediation', studentId]);
      queryClient.invalidateQueries(['student-narratives']);
      toast.success('Remediation story generated!');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Generation failed'),
  });

  const checkMut = useMutation({
    mutationFn: () => integrityAPI.checkPattern(studentId),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['remediation', studentId]);
      toast.success(res.data?.triggered ? 'Remediation triggered.' : 'No pattern detected.');
    },
  });

  const items = data?.items || [];

  return (
    <BrutalCard>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-xl font-black uppercase flex items-center gap-2">
          <BookOpen size={20} /> Remediation Stories
        </h3>
        <BrutalButton variant="ghost" size="sm" onClick={() => checkMut.mutate()}>
          Run pattern check
        </BrutalButton>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        When your child fails an assessment or shows repeated integrity signals, the system queues a custom
        remediation story that reuses the exact words they struggled with.
      </p>
      {isLoading && <p className="text-sm">Loading…</p>}
      {!isLoading && items.length === 0 && (
        <p className="text-sm text-emerald-600 font-bold">✓ No pending remediation. 🎉</p>
      )}
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="border-4 border-black p-3 bg-white">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1">
                  <p className="font-bold text-sm uppercase">
                    {r.theme} · <span className="text-gray-500">{r.status}</span>
                  </p>
                  <p className="text-xs text-gray-600">
                    Trigger: {r.trigger} · Target threshold: {r.required_pass_threshold}%
                  </p>
                  {r.word_bank_snapshot?.length > 0 && (
                    <p className="text-xs mt-1">
                      Word focus:{' '}
                      <span className="font-bold">
                        {r.word_bank_snapshot.slice(0, 8).join(', ')}
                        {r.word_bank_snapshot.length > 8 ? '…' : ''}
                      </span>
                    </p>
                  )}
                  {r.failed_concepts?.length > 0 && (
                    <p className="text-xs mt-1 text-rose-600">
                      Failed concepts: {r.failed_concepts.join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {!r.remediation_narrative_id && r.status === 'pending' && (
                    <BrutalButton
                      variant="indigo"
                      size="sm"
                      onClick={() => generateMut.mutate(r.id)}
                      disabled={generateMut.isPending}
                    >
                      {generateMut.isPending ? 'Generating…' : 'Generate Story'}
                    </BrutalButton>
                  )}
                  {r.remediation_narrative_id && (
                    <BrutalBadge variant="emerald" size="sm">Story ready</BrutalBadge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </BrutalCard>
  );
};

// Reads the student's ungraded reflection answers, grouped by axis.
// Highlights any rows the backend flagged for concerning content.
const ReflectionAnswersPanel = ({ studentId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['reflection-answers', studentId],
    queryFn: async () => (await reflectionAPI.list(studentId)).data,
    enabled: !!studentId,
  });

  if (isLoading) return null;
  const total = data?.total || 0;
  const byAxis = data?.by_axis || {};

  return (
    <BrutalCard>
      <h3 className="text-xl font-black uppercase mb-3 flex items-center gap-2">
        <Eye size={20} /> Character Reflection Answers
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        Your child's unscripted answers to ungraded reflection questions we ask between chapters.
        Grouped by character theme. These aren't right-or-wrong — they're windows.
      </p>
      {total === 0 ? (
        <p className="text-sm text-gray-500">
          No reflection answers yet. Your child will see these questions as they read through stories.
        </p>
      ) : (
        <div className="space-y-4">
          {Object.entries(byAxis).map(([axis, answers]) => (
            <div key={axis} className="border-l-4 border-black pl-3">
              <p className="font-black uppercase text-sm mb-2">{axis} ({answers.length})</p>
              <div className="space-y-2">
                {answers.slice(0, 5).map((a) => (
                  <div key={a.id} className={`border-2 border-black p-2 ${a.parent_flagged ? 'bg-rose-50' : 'bg-white'}`}>
                    <p className="text-sm italic">"{a.answer_text}"</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : ''}
                      {a.parent_flagged && <span className="ml-2 text-rose-600 font-bold">⚠ flagged for review</span>}
                    </p>
                  </div>
                ))}
                {answers.length > 5 && (
                  <p className="text-xs text-gray-500">… plus {answers.length - 5} more on this theme</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </BrutalCard>
  );
};

export default IntegrityReportTab;
